import { useEffect, useMemo, useState } from 'react';
import { ProductAggregateDocument, ProductVariantDocument } from '@/types/manage.types';

const ProductAttributesSelector = ({
  product,
  onVariantChange,
}: {
  product: ProductAggregateDocument;
  onVariantChange: (selectedVariant: ProductVariantDocument | null) => void;
}) => {
  // Combination of attributes selected by the user. Ex: { "quantity": "3", "filling": "beef" }
  const [selectedAttributes, setSelectedAttributes] = useState<{ [key: string]: string }>({});

  // Create a map of variants for quick lookup
  const variantMap = useMemo(() => {
    const map = new Map<string, ProductVariantDocument>();

    for (const variant of product.variants || []) {
      const key = variant.attribute_combination
        .map((attr) => `${attr.key}:${attr.value}`)
        .sort() // ← ensures consistent order
        .join('|');

      map.set(key, variant);
    }

    return map;
  }, [product.variants]);

  // Set default variant on mount
  useEffect(() => {
    if (Object.keys(selectedAttributes).length > 0) return; // Don't overwrite existing selection

    const defaultVariant = product.variants?.find((v) => v.is_default);
    if (defaultVariant) {
      const defaultAttrs: { [key: string]: string } = {};
      defaultVariant.attribute_combination.forEach((attr) => {
        defaultAttrs[attr.key] = attr.value;
      });
      setSelectedAttributes(defaultAttrs);
      onVariantChange(defaultVariant);
    }
  }, [onVariantChange, product.variants, selectedAttributes]);

  // When attribute selection changes, find matching variant
  useEffect(() => {
    if (!product.variants || Object.keys(selectedAttributes).length === 0) {
      onVariantChange(null);
      return;
    }

    // Create a unique key for the selected attributes
    const key = Object.entries(selectedAttributes)
      .map(([k, v]) => `${k}:${v}`)
      .sort()
      .join('|');

    const matched = variantMap.get(key) || null;
    onVariantChange(matched);
    // console.log({matched});
  }, [selectedAttributes, variantMap, onVariantChange, product.variants]);

  // Why is this removed?
  // Reason and explanation:
  // 1. The variantMap is created using useMemo, so it will only be created once
  // 2. The variantMap is used to find the matching variant, so it is more efficient
  // 3. The variantMap is created using the product.variants, so it will always be up to date

  // useEffect(() => {
  //   console.log({selectedAttributes});
  //   if (!product.variants || Object.keys(selectedAttributes).length === 0) return;

  //   const selectedKeys = Object.keys(selectedAttributes);

  //   const matched = product.variants.find((variant) => {
  //     if (variant.attribute_combination.length !== selectedKeys.length) return false;

  //     for (const attr of variant.attribute_combination) {
  //       if (selectedAttributes[attr.key] !== attr.value) {
  //         return false;
  //       }
  //     }

  //     return true;
  //   });

  //   onVariantChange(matched || null);
  // }, [selectedAttributes, product.variants, onVariantChange]);

  const handleAttributeChange = (key: string, value: string) => {
    setSelectedAttributes((prev) => ({ ...prev, [key]: value }));
  };

  // Build attribute options from product.attributes (not variants)
  const selectableAttributes = useMemo(() => {
    const result: Record<string, { value: string; label: string }[]> = {};

    for (const key of Object.keys(product.attributes)) {
      const options = product.attributes[key];
      // Ensure uniqueness by value
      const seen = new Set<string>();
      result[key] = [];

      for (const option of options) {
        const val = String(option.value);
        if (!seen.has(val)) {
          seen.add(val);
          result[key].push({ value: val, label: option.label });
        }
      }
    }

    return result;
  }, [product.attributes]);

  return (
    <div className="border-t pt-4">
      {/* Attribute Selection (Grouped by Key) */}
      {Object.entries(selectableAttributes).map(([key, values]) => (
        <div key={key} className="pb-4">
          <p className="text-xs text-gray-600 mb-2">{key.toLocaleUpperCase()}</p>
          <div className="flex flex-wrap gap-2">
            {values.map(({ value, label }) => (
              <ToggleButton
                key={value}
                label={label}
                selected={selectedAttributes[key] === value}
                onClick={() => handleAttributeChange(key, value)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Reusable ToggleButton for attribute values
const ToggleButton = ({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-2 py-1 rounded-lg border-2 transition-colors duration-200 ${
        selected
          ? 'border-orange-100 bg-orange-100 text-orange-800'
          : 'text-gray-700 border-transparent hover:border-orange-100'
      }`}>
      {label}
    </button>
  );
};

export default ProductAttributesSelector;
